import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Chip,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import useToast from "../../hooks/useToast";
import Toast from "../../components/Toast";
import {
  getRiders,
  createDeliverySheet,
  Rider,
} from "../../services/deliverySheetApi";
import {
  scanDeliverySheetConsignment,
  ScanConsignmentData,
} from "../../services/consignmentApi";

const DeliverySheetCreate = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { toast, showSuccess, showError, showWarning, hideToast } = useToast();
  const [riders, setRiders] = useState<Rider[]>([]);
  const [selectedRider, setSelectedRider] = useState<number | "">("");
  const [scanInput, setScanInput] = useState("");
  const [scanFeedback, setScanFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState<"success" | "error">(
    "success",
  );
  const [scannedConsignments, setScannedConsignments] = useState<
    ScanConsignmentData[]
  >([]);
  const [includedIds, setIncludedIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRiders();
  }, []);

  useEffect(() => {
    if (scanInput.length === 17 && selectedRider) {
      handleScan();
    }
  }, [scanInput]);

  const fetchRiders = async () => {
    try {
      const response = await getRiders();
      setRiders(response.data);
    } catch (error: any) {
      console.error("Error fetching riders:", error);
    }
  };

  const handleRiderChange = (riderId: number | "") => {
    setSelectedRider(riderId);
    // Clear scanned consignments when changing rider
    setScannedConsignments([]);
    setIncludedIds([]);
    setScanInput("");
    setScanFeedback("");
  };

  const handleScan = async () => {
    try {
      setScanFeedback("");
      const result = await scanDeliverySheetConsignment({
        consignment_no: scanInput,
      });

      if (result.success) {
        if (includedIds.includes(result.id)) {
          setScanFeedback("Consignment Already added.");
          setFeedbackType("error");
        } else {
          setScannedConsignments([...scannedConsignments, result.data]);
          setIncludedIds([...includedIds, result.id]);
          setScanFeedback(result.message);
          setFeedbackType("success");
          setScanInput("");
        }
      }
    } catch (error: any) {
      setScanFeedback(error.message || "Failed to scan consignment");
      setFeedbackType("error");
    }
  };

  const handleRemoveConsignment = (id: number) => {
    setScannedConsignments(scannedConsignments.filter((c) => c.id !== id));
    setIncludedIds(includedIds.filter((cid) => cid !== id));
  };

  const handleSubmit = async () => {
    if (!selectedRider) {
      showWarning("Please select a rider");
      return;
    }

    if (scannedConsignments.length === 0) {
      showWarning("Please scan at least one consignment");
      return;
    }

    try {
      setSubmitting(true);
      await createDeliverySheet({
        rider_id: selectedRider as number,
        consignmentIds: includedIds,
      });
      showSuccess("Delivery sheet created successfully!");
      setTimeout(() => {
        navigate("/delivery-sheets");
      }, 1500);
    } catch (error: any) {
      showError(error.message || "Failed to create delivery sheet");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("delivered")) return "success";
    if (statusLower.includes("failed")) return "error";
    if (statusLower.includes("pending") || statusLower.includes("booked"))
      return "warning";
    return "info";
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate("/dashboard")}
          sx={{ textDecoration: "none", color: theme.palette.text.primary }}
        >
          Home
        </MuiLink>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate("/delivery-sheets")}
          sx={{ textDecoration: "none", color: theme.palette.text.primary }}
        >
          Delivery Sheets
        </MuiLink>
        <Typography color="text.primary">Allocate Deliveries</Typography>
      </Breadcrumbs>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Allocate Deliveries
          </Typography>

          {/* Basic Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Basic Information
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: 400 }}>
              <InputLabel>Rider *</InputLabel>
              <Select
                value={selectedRider}
                onChange={(e) => handleRiderChange(e.target.value as number)}
                label="Rider *"
              >
                <MenuItem value="">Select Rider</MenuItem>
                {riders.map((rider) => (
                  <MenuItem key={rider.id} value={rider.id}>
                    {rider.name} ({rider.loginID})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Consignment Scanner - Only show when rider is selected */}
          {selectedRider && (
            <>
              <Box sx={{ my: 5, textAlign: "center" }}>
                <Typography variant="h6" gutterBottom>
                  Scan Consignments ({scannedConsignments.length})
                </Typography>
                <Box sx={{ maxWidth: 600, mx: "auto", mt: 3 }}>
                  <TextField
                    fullWidth
                    placeholder="Enter Consignment Number"
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    inputProps={{ maxLength: 17 }}
                    autoFocus
                    sx={{ mb: 2 }}
                  />
                  {scanFeedback && (
                    <Alert severity={feedbackType} sx={{ mb: 2 }}>
                      {scanFeedback}
                    </Alert>
                  )}
                </Box>
              </Box>

              {/* Scanned Consignments Table */}
              {scannedConsignments.length > 0 && (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mb: 3 }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Consignment #</TableCell>
                        <TableCell>Booking Type</TableCell>
                        <TableCell>Service Type</TableCell>
                        <TableCell>Booking Date</TableCell>
                        <TableCell>Weight (Grams)</TableCell>
                        <TableCell>COD Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scannedConsignments.map((consignment) => (
                        <TableRow key={consignment.id}>
                          <TableCell>{consignment.consignment_no}</TableCell>
                          <TableCell>{consignment.booking_type}</TableCell>
                          <TableCell>{consignment.service_type}</TableCell>
                          <TableCell>{consignment.created_at}</TableCell>
                          <TableCell>{consignment.weight}</TableCell>
                          <TableCell>{consignment.cod_amount ?? 0}</TableCell>
                          <TableCell>
                            <Chip
                              label={consignment.status}
                              color={getStatusColor(consignment.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleRemoveConsignment(consignment.id)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Submit Button */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/delivery-sheets")}
                  sx={{ mr: 2 }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={submitting || scannedConsignments.length === 0}
                >
                  {submitting ? "Creating..." : "Submit"}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Toast Notification */}
      <Toast
        open={toast.open}
        message={toast.message}
        title={toast.title}
        severity={toast.severity}
        onClose={hideToast}
      />
    </Box>
  );
};

export default DeliverySheetCreate;
